import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Header.css';

const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'LPA Types', to: '/#types', isHash: true },
  { label: 'Court of Protection', to: '/court-of-protection' },
  { label: 'Fees', to: '/lpa-fees' },
  { label: 'FAQs', to: '/lasting-power-of-attorney-faqs' },
  { label: 'About', to: '/about-charles-lossos' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 8);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  const handleHashClick = useCallback(
    (e, to) => {
      e.preventDefault();
      const [path, hash] = to.split('#');
      const targetPath = path || '/';

      const scrollToHash = () => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      };

      if (location.pathname === targetPath) {
        scrollToHash();
      } else {
        navigate(to);
      }
      setMenuOpen(false);
    },
    [location.pathname]
  );

  const isActive = useCallback(
    (to) => {
      if (to.includes('#')) return false;
      if (to === '/' && location.hash) return false;
      return location.pathname === to;
    },
    [location.pathname, location.hash]
  );

  const renderNavLink = (item, isMobile = false) => {
    const className = isMobile
      ? `header__mobile-nav-link${isActive(item.to) ? ' header__mobile-nav-link--active' : ''}`
      : `header__nav-link${isActive(item.to) ? ' header__nav-link--active' : ''}`;

    if (item.isHash) {
      return (
        <a key={item.label} href={item.to} className={className}
          onClick={(e) => handleHashClick(e, item.to)}>
          {item.label}
        </a>
      );
    }
    return (
      <Link key={item.label} to={item.to} className={className}
        onClick={() => setMenuOpen(false)}>
        {item.label}
      </Link>
    );
  };

  return (
    <header className={`header${scrolled ? ' header--scrolled' : ''}`}>
      <div className="header__inner">
        {/* Inline SVG Logo */}
        <Link to="/" className="header__logo" onClick={() => setMenuOpen(false)}>
          <svg className="header__logo-svg" viewBox="0 0 640 120" xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision">
            {/* "THE" with side lines */}
            <line x1="196" y1="20" x2="254" y2="20" stroke="#1e3a5f" strokeWidth="1.5"/>
            <text x="320" y="28" textAnchor="middle" fontFamily="'Playfair Display', Georgia, serif" fontSize="20" fontWeight="400" letterSpacing="10" fill="#152a4a">THE</text>
            <line x1="386" y1="20" x2="444" y2="20" stroke="#1e3a5f" strokeWidth="1.5"/>

            {/* Line above main text */}
            <line x1="60" y1="38" x2="580" y2="38" stroke="#1e3a5f" strokeWidth="1.5"/>

            {/* "LPA COMPANY" */}
            <text x="320" y="80" textAnchor="middle" fontFamily="'Playfair Display', Georgia, serif" fontSize="52" fontWeight="700" letterSpacing="6" fill="#152a4a">LPA COMPANY</text>

            {/* Line below main text */}
            <line x1="60" y1="90" x2="580" y2="90" stroke="#1e3a5f" strokeWidth="1.5"/>

            {/* "Lasting Powers of Attorney" */}
            <text x="320" y="114" textAnchor="middle" fontFamily="'Playfair Display', Georgia, serif" fontStyle="italic" fontSize="26" fontWeight="400" fill="#5a6a7a">Lasting Powers of Attorney</text>
          </svg>
        </Link>

        {/* Desktop Navigation */}
        <nav className="header__nav" aria-label="Main navigation">
          <ul className="header__nav-links">
            {NAV_ITEMS.map((item) => (
              <li key={item.label}>{renderNavLink(item)}</li>
            ))}
          </ul>
          <Link to="/contact" className="header__cta">
            Contact Us
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className={`header__hamburger${menuOpen ? ' header__hamburger--open' : ''}`}
          onClick={toggleMenu}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <span className="header__hamburger-line" />
          <span className="header__hamburger-line" />
          <span className="header__hamburger-line" />
        </button>
      </div>

      {/* Mobile Menu */}
      <nav
        className={`header__mobile-menu${menuOpen ? ' header__mobile-menu--open' : ''}`}
        aria-label="Mobile navigation"
        aria-hidden={!menuOpen}
      >
        <ul className="header__mobile-nav-links">
          {NAV_ITEMS.map((item) => (
            <li key={item.label}>{renderNavLink(item, true)}</li>
          ))}
        </ul>
        <Link to="/contact" className="header__mobile-cta" onClick={() => setMenuOpen(false)}>
          Contact Us
        </Link>
      </nav>
    </header>
  );
};

export default Header;
